import { browser } from "$app/env";
import { get, put } from "#lib/api-client.js";
import type { UserSettingsResponse } from "#lib/api-client/types.gen.js";

/**
 * Cache key prefix. Settings are per user and cached per user id so a different
 * account on the same browser never inherits somebody else's preferences.
 */
const CACHE_PREFIX = "userSettings:";

/**
 * User settings are one server-side JSON object per user, persisted through
 * `GET/PUT /profile/settings`. The frontend treats the blob as a flat namespace
 * of keys (for example `dashboard.collector.pinnedTab`) and stores it
 * optimistically: local state and the localStorage cache update immediately,
 * then the whole object is written back in order.
 */
class UserSettingsStore {
	values = $state<Record<string, unknown>>({});
	/** True once the server copy has been merged in (or the server said "nothing"). */
	loaded = $state(false);
	loading = $state(false);
	error = $state<string | null>(null);

	#userId: string | null = null;
	#saveQueue: Promise<void> = Promise.resolve();
	#saveSeq = 0;

	/**
	 * Loads the user's settings once per signed-in user. Cached values are shown
	 * immediately while the request runs so the UI never flickers on boot.
	 */
	async load(userId: string): Promise<void> {
		if (!browser || !userId) return;
		if (this.#userId === userId && (this.loaded || this.loading)) return;
		this.#userId = userId;
		this.loaded = false;
		this.error = null;
		try {
			const cached = localStorage.getItem(CACHE_PREFIX + userId);
			if (cached) {
				this.values = JSON.parse(cached) as Record<string, unknown>;
			}
		} catch {
			this.values = {};
		}

		this.loading = true;
		try {
			const response = await get<UserSettingsResponse>("/profile/settings");
			this.values = response.settings ?? {};
			this.#cache();
			this.loaded = true;
		} catch (err) {
			// The cached copy keeps the UI usable; surface the failure without
			// blocking the app.
			this.error = err instanceof Error ? err.message : "Failed to load settings";
		} finally {
			this.loading = false;
		}
	}

	/** Reads a setting, returning fallback when it has never been written. */
	get<T>(key: string, fallback: T): T {
		const value = this.values[key];
		return (value === undefined ? fallback : value) as T;
	}

	/** Sets a setting and persists the whole object. */
	async set(key: string, value: unknown): Promise<void> {
		if (!browser || !this.#userId) return;
		this.values = { ...this.values, [key]: value };
		this.#cache();
		this.#enqueueSave();
	}

	/** Removes a setting and persists the whole object. */
	async unset(key: string): Promise<void> {
		if (!browser || !this.#userId) return;
		const next = { ...this.values };
		delete next[key];
		this.values = next;
		this.#cache();
		this.#enqueueSave();
	}

	/** Forgets the in-memory settings (used on logout and in tests). */
	clear(): void {
		this.values = {};
		this.loaded = false;
		this.loading = false;
		this.error = null;
		this.#userId = null;
	}

	#cache(): void {
		if (!browser || !this.#userId) return;
		localStorage.setItem(CACHE_PREFIX + this.#userId, JSON.stringify(this.values));
	}

	/**
	 * Saves are serialized so two quick pin clicks cannot land out of order: each
	 * write is the values snapshot at the time it was queued.
	 */
	#enqueueSave(): void {
		const seq = ++this.#saveSeq;
		const snapshot = { ...this.values };
		this.#saveQueue = this.#saveQueue
			.then(async () => {
				const response = await put<UserSettingsResponse>("/profile/settings", {
					settings: snapshot,
				});
				// Only adopt the response for the newest queued write: an older
				// response would otherwise roll the UI back to a stale snapshot while
				// a newer save is still in flight.
				if (seq === this.#saveSeq && response?.settings) {
					this.values = response.settings;
					this.#cache();
				}
			})
			.catch((err: unknown) => {
				this.error = err instanceof Error ? err.message : "Failed to save settings";
			});
	}
}

export const userSettings = new UserSettingsStore();

/** Setting key for the collector dashboard's default tab. */
export const COLLECTOR_PINNED_TAB = "dashboard.collector.pinnedTab";
/** Setting key for the processor dashboard's default tab. */
export const PROCESSOR_PINNED_TAB = "dashboard.processor.pinnedTab";
